import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, Pause, ShoppingCart, ArrowLeft, BadgeCheck, Loader2, MessageSquare, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { StarRating } from "../components/StarRating";
import LicenseModal from "../components/LicenseModal";
import FreeBeatModal from "../components/FreeBeatModal";
import { getPricing } from "../lib/pricing";
import { useAudioPlayer } from "../contexts/AudioPlayerContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => (url?.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);
const formatDate = (iso) => new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

export default function BeatDetail() {
  const { id } = useParams();
  const { currentTrack, isPlaying, playTrack } = useAudioPlayer();
  const [beat, setBeat] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [freeModalOpen, setFreeModalOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", rating: 0, text: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    const { data } = await axios.get(`${BACKEND_URL}/api/beats/${id}/comments`);
    setComments(data);
  }, [id]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/beats/${id}`);
        setBeat(data);
        await fetchComments();
      } catch {
        setBeat(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, fetchComments]);

  const avgRating = comments.length
    ? (comments.reduce((a, c) => a + c.rating, 0) / comments.length).toFixed(1)
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.rating) return toast.error("Please select a star rating");
    if (!form.text.trim()) return toast.error("Please write a comment");
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/beats/${id}/comments`, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        rating: form.rating,
        text: form.text.trim(),
      });
      toast.success("Comment posted!");
      setForm({ name: "", email: "", rating: 0, text: "" });
      fetchComments();
    } catch {
      toast.error("Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!beat) {
    return (
      <div className="pt-32 pb-12 px-6 text-center" data-testid="beat-not-found">
        <p className="text-muted-foreground text-lg mb-4">Beat not found.</p>
        <Link to="/store">
          <Button variant="outline" className="rounded-full">Back to Store</Button>
        </Link>
      </div>
    );
  }

  const isThisPlaying = currentTrack?.id === beat.id && isPlaying;
  const pricing = getPricing(beat);

  return (
    <div className="pt-24 pb-12 px-6 lg:px-8" data-testid="beat-detail-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/store" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="back-to-store">
          <ArrowLeft className="w-4 h-4" /> Back to Store
        </Link>

        {/* Beat header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row gap-6 bg-card border border-border rounded-2xl p-6 mb-10"
        >
          <div className="relative flex-shrink-0 mx-auto sm:mx-0">
            <img
              src={resolveUrl(beat.cover_url)}
              alt={beat.title}
              className="w-48 h-48 rounded-2xl object-cover"
            />
            <Button
              onClick={() => playTrack(beat, [beat])}
              size="icon"
              className="absolute inset-0 m-auto w-14 h-14 rounded-full bg-black/60 hover:bg-primary"
              data-testid="detail-play-btn"
            >
              {isThisPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
            </Button>
          </div>
          <div className="flex-1 flex flex-col">
            <h1 className="font-heading text-3xl lg:text-4xl font-black">{beat.title}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {(beat.genres?.length ? beat.genres : [beat.genre]).map((g) => (
                <Badge key={g} variant="outline">{g}</Badge>
              ))}
              <Badge variant="secondary" className="font-mono">{beat.bpm} BPM</Badge>
              <Badge variant="secondary" className="font-mono">{beat.key}</Badge>
            </div>
            {avgRating && (
              <div className="flex items-center gap-2 mt-4">
                <StarRating value={Math.round(avgRating)} readOnly size={18} />
                <span className="text-sm text-muted-foreground">{avgRating} · {comments.length} review{comments.length !== 1 ? "s" : ""}</span>
              </div>
            )}
            <div className="mt-auto pt-6 flex items-center justify-between">
              {pricing.isFree ? (
                <Badge className="bg-green-500/20 text-green-400 font-bold text-base px-3 py-1">FREE</Badge>
              ) : pricing.hasDiscount ? (
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-lg text-muted-foreground line-through">${pricing.original}</span>
                  <span className="font-heading font-black text-2xl text-primary">${pricing.final}</span>
                  <Badge className="bg-primary text-white text-xs">-{pricing.discount}%</Badge>
                </div>
              ) : (
                <span className="font-heading font-black text-2xl text-primary">${pricing.original}</span>
              )}
              {pricing.isFree ? (
                <Button
                  onClick={() => setFreeModalOpen(true)}
                  className="rounded-full bg-primary hover:bg-primary/90 neon-glow gap-2"
                  data-testid="detail-free-btn"
                >
                  <Download className="w-4 h-4" /> Download Free
                </Button>
              ) : (
                <Button
                  onClick={() => setModalOpen(true)}
                  className="rounded-full bg-primary hover:bg-primary/90 neon-glow gap-2"
                  data-testid="detail-buy-btn"
                >
                  <ShoppingCart className="w-4 h-4" /> Buy License
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Comments */}
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2 mb-6">
            <MessageSquare className="w-6 h-6 text-primary" /> Comments
            <span className="text-muted-foreground font-normal text-lg">({comments.length})</span>
          </h2>

          {/* Add comment form */}
          <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-6 mb-8 space-y-4" data-testid="comment-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Your Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                  className="rounded-xl bg-background"
                  data-testid="comment-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email <span className="text-xs text-muted-foreground font-normal">(optional — verifies purchase)</span></Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@email.com"
                  className="rounded-xl bg-background"
                  data-testid="comment-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Your Rating</Label>
              <StarRating value={form.rating} onChange={(r) => setForm({ ...form, rating: r })} size={26} />
            </div>
            <div className="space-y-2">
              <Label>Comment</Label>
              <Textarea
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Share your thoughts on this beat..."
                className="rounded-xl bg-background min-h-24"
                data-testid="comment-text-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-primary hover:bg-primary/90 gap-2"
              data-testid="comment-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Post Comment
            </Button>
          </form>

          {/* Comment list */}
          {comments.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No comments yet. Be the first to review this beat!</p>
          ) : (
            <div className="space-y-4">
              {comments.map((c) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card border border-border rounded-2xl p-5"
                  data-testid={`comment-${c.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{c.name}</span>
                      {c.verified && (
                        <Badge className="bg-green-500/20 text-green-400 gap-1 text-xs" data-testid="verified-badge">
                          <BadgeCheck className="w-3 h-3" /> Verified Buyer
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(c.created_at)}</span>
                  </div>
                  <StarRating value={c.rating} readOnly size={14} />
                  <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{c.text}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <LicenseModal beat={beat} open={modalOpen} onClose={() => setModalOpen(false)} />
      <FreeBeatModal beat={beat} open={freeModalOpen} onClose={() => setFreeModalOpen(false)} />
    </div>
  );
}
